import { useDispatch, useSelector } from 'react-redux';

import {
  resetSessionState,
  SessionState,
  setAutoConnect,
  setAvatarReady,
  setCompanionData,
  setCompanionSlug,
  setCreditBalance,
  setDataChannel,
  setIsConnecting,
  setIsDesignerSession,
  setIsFileUploading,
  setIsTwinUpdating,
  setPrevMentorId,
  setPrevSocketId,
  setPromptData,
  setSecondsBalance,
  setSessionId,
  setSocketId,
  setTaskId,
  setTranscriptTime,
  setWebRTCStatus,
} from '../../store/reducers/sessionReducer';
import { AppDispatch, RootState } from '../../store/store';
import { SessionMentor } from '../../types/mentor';

export const useAudioVideo = () => {
  const webRTCStatus = useSelector(
    (state: RootState) => state.sessionReducer.webRTCStatus,
  );

  const dispatch = useDispatch();

  return {
    webRTCStatus,
    setWebRTCStatus: (webRTCStatus: SessionState['webRTCStatus']) =>
      dispatch(setWebRTCStatus(webRTCStatus)),
  };
};

export const useSessionFeatureState = () => {
  const state = useSelector((state: RootState) => state.sessionReducer);
  const dispatch: AppDispatch = useDispatch();

  return {
    promptData: state.promptData,
    secondsBalance: state.secondsBalance,
    creditBalance: state.creditBalance,
    companionSlug: state.companionSlug,
    companionData: state.companionData,
    avatarReady: state.avatarReady,
    transcriptTime: state.transcriptTime,
    dataChannel: state.dataChannel,
    sessionId: state.sessionId,
    taskId: state.taskId,
    isDesignerSession: state.isDesignerSession,
    isFileUploading: state.isFileUploading,
    isSessionPage: state.isSessionPage,
    isConnecting: state.isConnecting,
    autoConnect: state.autoConnect,
    socketId: state.socketId,
    prevMentorId: state.prevMentorId,
    prevSocketId: state.prevSocketId,
    isTwinUpdating: state.isTwinUpdating,
    setIsFileUploading: (isFileUploading: boolean) =>
      dispatch(setIsFileUploading(isFileUploading)),
    setCompanionSlug: (payload: {
      companionSlug: string | null;
      isSessionPage: boolean;
      autoConnect?: boolean;
    }) => dispatch(setCompanionSlug(payload)),
    setCompanionData: (companionData: SessionMentor | null) =>
      dispatch(setCompanionData(companionData)),
    setPromptData: (promptData: string) => dispatch(setPromptData(promptData)),
    setSecondsBalance: (secondsBalance: number | null) =>
      dispatch(setSecondsBalance(secondsBalance)),
    setCreditBalance: (creditBalance: number | null) =>
      dispatch(setCreditBalance(creditBalance)),
    setAvatarReady: (avatarReady: boolean) =>
      dispatch(setAvatarReady(avatarReady)),
    setTranscriptTime: (transcriptTime: number | null) =>
      dispatch(setTranscriptTime(transcriptTime)),
    setDataChannel: (dataChannel: RTCDataChannel | null) =>
      dispatch(setDataChannel(dataChannel)),
    setSessionId: (sessionId: string | undefined) =>
      dispatch(setSessionId(sessionId)),
    setTaskId: (taskId: string | null) => dispatch(setTaskId(taskId)),
    setIsDesignerSession: (isDesignerSession: boolean) =>
      dispatch(setIsDesignerSession(isDesignerSession)),
    setIsConnecting: (isConnecting: boolean) =>
      dispatch(setIsConnecting(isConnecting)),
    setAutoConnect: (autoConnect: boolean) =>
      dispatch(setAutoConnect(autoConnect)),
    setSocketId: (socketId: string | null) => dispatch(setSocketId(socketId)),
    setPrevMentorId: (prevMentorId: string | null) =>
      dispatch(setPrevMentorId(prevMentorId)),
    setPrevSocketId: (prevSocketId: string | null) =>
      dispatch(setPrevSocketId(prevSocketId)),
    setWebRTCStatus: (webRTCStatus: SessionState['webRTCStatus']) =>
      dispatch(setWebRTCStatus(webRTCStatus)),
    setIsTwinUpdating: (isTwinUpdating: boolean) =>
      dispatch(setIsTwinUpdating(isTwinUpdating)),
    resetSessionState: (full: boolean) => dispatch(resetSessionState(full)),
  };
};
